import React from 'react';
import './Scalfoldinghirepage.css';
import QuoteForm from '../../components/QuoteForm';
import SEO from '../../components/SEO';

const Scalfoldinghirepage = () => {

  const scaffoldingTypes = [
    {
      title: 'Frame Scaffolding',
      description: 'Versatile and cost-effective solution for standard construction projects. Easy to assemble and highly stable.',
      icon: '🏗️'
    },
    {
      title: 'Mobile Scaffolding Towers',
      description: 'Portable and adjustable scaffolding perfect for projects requiring frequent repositioning. Equipped with wheels for easy movement.',
      icon: '🚚'
    },
    {
      title: 'Suspended Scaffolding',
      description: 'Ideal for high-rise buildings and exterior work. Suspended from the roof with adjustable platform heights.',
      icon: '🏢'
    },
    {
      title: 'Cantilever Scaffolding',
      description: 'Specialized solution for projects where ground support is limited. Anchored to the building structure.',
      icon: '🔧'
    },
    {
      title: 'Custom Scaffolding Solutions',
      description: 'Tailored scaffolding systems designed for unique project requirements and challenging site conditions.',
      icon: '📐'
    }
  ];

  const whatsIncluded = [
    { icon: '🚛', title: 'Delivery & Setup', description: 'Professional delivery and installation services' },
    { icon: '🔨', title: 'Dismantling Services', description: 'Safe and efficient removal after project completion' },
    { icon: '✅', title: 'Safety Inspection', description: 'Comprehensive safety checks before handover' },
    { icon: '📏', title: 'Adjustable Heights', description: 'Flexible height configurations for any project' },
    { icon: '🌧️', title: 'Weather-resistant Materials', description: 'Durable materials built to withstand Kenyan weather' },
    { icon: '👷', title: 'On-site Support', description: 'Technical support throughout your rental period' }
  ];

  const applications = [
    { icon: '🏗️', title: 'Building Construction', description: 'Multi-story residential and commercial construction projects' },
    { icon: '🎨', title: 'Painting & Finishing', description: 'Interior and exterior painting with safe access solutions' },
    { icon: '🏠', title: 'Ceiling Installation', description: 'Perfect for gypsum ceiling and drywall installation projects' },
    { icon: '🔧', title: 'Maintenance & Repairs', description: 'Building maintenance, repairs, and renovation work' },
    { icon: '🌳', title: 'Exterior Works', description: 'Facades, cladding, and exterior finishing works' }
  ];

  const faqs = [
    {
      question: 'How much does scaffolding hire cost in Kenya?',
      answer: 'Pricing depends on the type of scaffolding, project size, duration, and specific requirements. Contact us for a customized quote tailored to your project needs.'
    },
    {
      question: 'How long can I hire scaffolding?',
      answer: 'We offer flexible rental periods ranging from daily, weekly, to monthly rates. You can extend your rental as needed with advance notice.'
    },
    {
      question: 'Do you provide installation services?',
      answer: 'Yes! We provide complete delivery, installation, and dismantling services performed by our trained professionals to ensure maximum safety.'
    },
    {
      question: 'Is your scaffolding safe and certified?',
      answer: 'Absolutely. All our scaffolding systems meet industry safety standards, use certified materials, and undergo regular inspections. Our installers are trained professionals.'
    },
    {
      question: 'Can I hire scaffolding for small residential projects?',
      answer: 'Yes, we provide scaffolding solutions for projects of all sizes, from small residential renovations to large-scale commercial construction.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Scaffolding Hire Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "telephone": "+254748600208",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": "Kenya",
    "description": "Professional scaffolding hire services in Nairobi Kenya. Frame scaffolding, mobile towers, suspended scaffolding with delivery, setup, and safety inspection included."
  };

  return (
    <div className="scaffolding-page">
      {/* SEO Component */}
      <SEO
        title="Scaffolding Hire Services in Nairobi Kenya | Techie Interiors"
        description="Reliable scaffolding hire in Nairobi Kenya - frame scaffolding, mobile towers, suspended scaffolding for construction, painting, and maintenance projects. Delivery and setup included."
        keywords="scaffolding hire Nairobi, scaffolding rental Kenya, construction scaffolding Nairobi, mobile scaffolding towers, suspended scaffolding Kenya, scaffolding services, affordable scaffolding, cheap scaffolding, professional scaffolding, top scaffolding services, scaffolding Nairobi, rental scaffolding Kenya, construction scaffolding Nairobi, mobile scaffolding towers, suspended scaffolding Kenya, scaffolding services, affordable scaffolding, cheap scaffolding, professional scaffolding, top scaffolding services, reliable scaffolding, experienced scaffolding, quality scaffolding, best scaffolding, leading scaffolding, modern scaffolding, scaffolding Nairobi, rental scaffolding Kenya, construction scaffolding Nairobi, mobile scaffolding towers, suspended scaffolding Kenya, scaffolding services, affordable scaffolding, cheap scaffolding, professional scaffolding, top scaffolding services, reliable scaffolding, experienced scaffolding, quality scaffolding, best scaffolding, leading scaffolding, modern scaffolding, scafolding quote"
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/scafolding-hire"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="scaffolding-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Professional Scaffolding for Hire in Kenya – Safe, Reliable & Affordable</h1>
          <p>High-quality scaffolding solutions for construction, renovation, painting, and maintenance projects.</p>
          <div className="hero-buttons">
            <a href="#quote-form" className="btn btn-primary">Request a Quote</a>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="scaffolding-intro">
        <div className="container">
          <p className="intro-text">
            We provide professional <strong>scaffolding for hire</strong> services designed to support residential, commercial, and industrial construction projects across Kenya. Our scaffolding systems are built with safety, durability, and efficiency in mind—ensuring your project runs smoothly from start to finish. Whether you need <strong>construction scaffolding</strong>, <strong>temporary access solutions</strong>, or <strong>safe scaffolding systems</strong> for your next project, we have you covered.
          </p>
        </div>
      </section>

      {/* Types of Scaffolding */}
      <section className="scaffolding-types">
        <div className="container">
          <h2 className="section-title">Types of Scaffolding We Offer</h2>
          <div className="scaffolding-grid">
            {scaffoldingTypes.map((type, index) => (
              <div key={index} className="scaffolding-card">
                <div className="card-icon">{type.icon}</div>
                <h3>{type.title}</h3>
                <p>{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="scaffolding-included">
        <div className="container">
          <h2 className="section-title">What's Included</h2>
          <div className="included-grid">
            {whatsIncluded.map((item, index) => (
              <div key={index} className="included-item">
                <div className="included-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="scaffolding-applications">
        <div className="container">
          <h2 className="section-title">Applications & Use Cases</h2>
          <div className="applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="application-card">
                <div className="app-icon">{app.icon}</div>
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Compliance */}
      <section className="scaffolding-safety">
        <div className="container">
          <div className="safety-content">
            <h2 className="section-title">Safety & Compliance</h2>
            <p className="safety-text">
              Our scaffolding systems meet industry safety standards and are installed by trained professionals to ensure maximum stability and worker protection.
            </p>
            <div className="safety-features">
              <div className="safety-item">
                <i className="fas fa-certificate"></i>
                <span>Certified Materials</span>
              </div>
              <div className="safety-item">
                <i className="fas fa-user-shield"></i>
                <span>Trained Installers</span>
              </div>
              <div className="safety-item">
                <i className="fas fa-clipboard-check"></i>
                <span>Regular Inspections</span>
              </div>
              <div className="safety-item">
                <i className="fas fa-hard-hat"></i>
                <span>Safety Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Client Request Form */}
      <section className="scaffolding-form-section" id="quote-form">
        <div className="container">
          <h2 className="section-title">Request a Scaffolding Quote</h2>
          <QuoteForm serviceName="Scaffolding for Hire" />
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="scaffolding-seo">
        <div className="container">
          <p className="seo-text">
            We are a trusted provider of <strong>scaffolding for hire in Kenya</strong>, offering durable and customizable scaffolding systems for all types of construction and interior projects. Whether you need access solutions for gypsum ceiling installation, drywall work, or large-scale building construction, we deliver safe and efficient scaffolding tailored to your needs. Our <strong>construction scaffolding services</strong> include professional installation, regular safety inspections, and competitive rental rates. Looking for <strong>scaffolding rental near me</strong>? We've got you covered with reliable <strong>temporary access solutions</strong> across Kenya.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="scaffolding-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Scalfoldinghirepage;
